import { motion } from "framer-motion";

/* Real playing-card motif. ♥ and ♦ use rosa-mexicano accent; ♠ and ♣ stay white. */
const cards = [
  {
    n: "A",
    suit: "♠",
    suitName: "Spades",
    title: "Analizar",
    text:
      "Diagnosticamos tu situación actual y los desafíos únicos de tu destino o proyecto para identificar oportunidades reales y medibles.",
    accent: false,
    rotation: "-rotate-[6deg] md:-rotate-[5deg]",
  },
  {
    n: "A",
    suit: "♥",
    suitName: "Hearts",
    title: "Aprender",
    text:
      "Formación y capacitación que entrega el conocimiento necesario para implementar soluciones creativas y eficaces.",
    accent: true,
    rotation: "-rotate-[2deg] md:rotate-[2deg]",
  },
  {
    n: "A",
    suit: "♦",
    suitName: "Diamonds",
    title: "Adaptar",
    text:
      "Ajustamos cada estrategia a las necesidades cambiantes del mercado, del viajero y de tu propio negocio.",
    accent: true,
    rotation: "rotate-[3deg] md:-rotate-[2deg]",
  },
  {
    n: "A",
    suit: "♣",
    suitName: "Clubs",
    title: "Actuar",
    text:
      "Ejecutamos con precisión y rapidez para maximizar el impacto comercial y de marca de cada acción.",
    accent: false,
    rotation: "rotate-[5deg] md:rotate-[6deg]",
  },
];

function PokerCard({ card, i }) {
  const accentColor = card.accent ? "var(--tn-accent)" : "#1a1a1a";
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ y: -10, rotate: 0, transition: { duration: 0.35 } }}
      data-testid={`poker-card-${card.title.toLowerCase()}`}
      className={`group relative aspect-[5/7] w-full rounded-md md:rounded-lg shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden transform ${card.rotation} transition-transform`}
      style={{
        backgroundColor: "#ecdfc7",
        color: "#1a1a1a",
        backgroundImage:
          "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(0,0,0,0.04) 100%)",
        border: "1px solid rgba(0,0,0,0.18)",
      }}
    >
      {/* Subtle paper texture */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='1'/></svg>\")",
        }}
      />

      {/* Inner double border */}
      <div
        aria-hidden
        className="absolute inset-2 md:inset-3 border border-black/15 pointer-events-none rounded-sm"
      />

      {/* Top-left: A + suit */}
      <div
        className="absolute top-4 left-4 md:top-5 md:left-5 flex flex-col items-center leading-none font-display"
        style={{ color: accentColor }}
      >
        <span className="font-black text-2xl md:text-3xl">{card.n}</span>
        <span className="text-xl md:text-2xl mt-0.5">{card.suit}</span>
      </div>

      {/* Bottom-right: A + suit (rotated) */}
      <div
        className="absolute bottom-4 right-4 md:bottom-5 md:right-5 flex flex-col items-center leading-none font-display rotate-180"
        style={{ color: accentColor }}
      >
        <span className="font-black text-2xl md:text-3xl">{card.n}</span>
        <span className="text-xl md:text-2xl mt-0.5">{card.suit}</span>
      </div>

      {/* Center: giant suit symbol + title */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
        <span
          className="font-display leading-none select-none mb-3 md:mb-5 drop-shadow-sm"
          style={{
            color: accentColor,
            fontSize: "clamp(4rem, 9vw, 7rem)",
            opacity: 0.95,
          }}
          aria-hidden
        >
          {card.suit}
        </span>
        <h3
          className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter"
          style={{ color: "#1a1a1a" }}
        >
          {card.title}
        </h3>
        <p
          className="mt-3 text-[11px] md:text-xs leading-snug max-w-[26ch]"
          style={{ color: "rgba(26,26,26,0.7)" }}
        >
          {card.text}
        </p>
      </div>
    </motion.article>
  );
}

export default function PokerDeAs() {
  return (
    <section
      id="metodologia"
      data-testid="poker-section"
      className="relative bg-[#080808] py-28 md:py-40 border-t border-white/10 overflow-hidden"
    >
      {/* Subtle felt-table radial */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 50% 60%, rgba(228,0,124,0.06), transparent 55%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-20">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/55">
              <span className="h-px w-8 bg-accent" />
              <span className="text-accent">Metodología</span>
              <span className="font-mono text-white/30">/ 01</span>
            </div>
            <h2 className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9]">
              Póker <br />
              <span className="text-white/25 italic font-light">de</span>{" "}
              <span className="relative inline-block">
                Ases
                <span
                  aria-hidden
                  className="absolute -bottom-2 left-0 right-0 h-[4px] bg-accent"
                />
              </span>
              .
            </h2>
          </div>
          <div className="md:col-span-6 flex items-end">
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
              Cuatro cartas. Una sola jugada ganadora. La creatividad{" "}
              <em className="not-italic text-white">debe</em> ir acompañada de
              un modelo de negocio sólido —{" "}
              <span className="text-white">
                Analiza, Aprende, Adapta, Actúa
              </span>{" "}
              — para que cada estrategia destaque{" "}
              <strong className="text-white">y genere beneficios tangibles</strong>.
            </p>
          </div>
        </div>

        {/* Cards row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 px-2 md:px-6">
          {cards.map((c, i) => (
            <PokerCard key={`${c.title}-${i}`} card={c} i={i} />
          ))}
        </div>

        {/* Closing strip */}
        <div className="mt-20 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-white/45">
          <span>Reparte la mano. Lleva la mesa.</span>
          <a
            href="#servicios"
            className="text-white/75 hover:text-accent transition-colors"
            data-testid="poker-cta"
          >
            Ver servicios →
          </a>
        </div>
      </div>
    </section>
  );
}
